/**
 * This object allows us to specify assets in one of the four Asset Set types
 * we know how to download: a cart, a search, a category or some bare assets.
 * (Other types may be added in the future.)
 * @class
 * @extends Netx.Model
 * @todo Finish documentation of defaults, methods and parsers
 */
import Base from '@netx/core/lib/netx-model';

import DownloadOptionsCollection from '@netx/core-share/collections/downloadOptions';
import ShareableModel from '@netx/core-share/models/shareable';

import Rpc from 'core-libs/backbone.rpc';

import {i18n} from '@netx/core/i18n';

import {types as assetSetTypes} from '../properties/assetSets';
import {types as downloadOptionTypes} from '@netx/core-share/properties/downloadOptions';
import {allSavedSearches} from '@netx/core-search/collections/singleton/savedSearch';

export default class AssetSet extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * @property {object}  defaults                - The default attribute values
	 * @property {array}   defaults.ids            - The default ids
	 * @property {boolean} defaults.recursive      - The default recursiveness. For completeness, the `category` type needs this extra info. Other types ignore this property.
	 * @property {string}  defaults.type           - The default type. See assetSetProps.types
	 *
	 * @property {array}   defaults.assetIds       - The default asset ids belonging to set (client side)
	 * @property {string}  defaults.label          - The default label. It's useful to set a label for the set when it's created, since it feeds into a generic system (client side)
	 * @property {array}   defaults.models         - The default models. Handy to attach the original models/collections, if we had them (client side)
	 * @property {boolean} defaults.canCreateLink  - The default description (client side)
	 * @property {boolean} defaults.canEmail       - The default description (client side)
	 */
	get defaults() {
		return {
			ids: [],
			recursive: false,
			type: '',

			// Client
			assetCount: 0,
			assetIds: [],
			canCreateLink: null,
			canEmail: null,
			label: '',
			models: [],
		};
	}

	/** @type {array} */
	get clientAttrs() {
		return [
			'assetIds',
			'assetCount',
			'label',
			'models',
			'canCreateLink',
			'canEmail',
		];
	}

	/**
	 * @property {object}  methods                      - Model RPC methods
	 * @property {method}  methods.getAssetIdsInSets    - Get all asset ids in asset set
	 * @property {method}  methods.getDownloadWarnings  - Get any download warnings for asset set
	 * @property {method}  methods.getDownloadOptions   - Get any download options for asset set
	 * @property {method}  methods.prepareDownload      - Prepare asset set download
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			getAssetIdsInSets(opts) {
				return ['getAssetIdsInSets', [this.self()]];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			getDownloadWarnings(opts) {
				return ['getDownloadWarnings', [this.self()]];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			getDownloadOptions(opts) {
				return ['getDownloadOptions', [this.self()]];
			},
			/**
			 * Request the DAM prepare a set of assets (asset lists, categories, saved searches, carts) in any of several formats (as originals, proxies, views or preset processes).
			 * If download is authorized, this returns a progress UUID for tracking the progress of this request.
			 * If download is prohibited due to a missing justification, this throws an exception that becomes a JSON-RPC error object.
			 * If download is prohibited due to approval, this triggers the server-side asset approval processes for the requested assets
			 *     (sending email, creating categories, etc -- all the things that requestDownloadApproval does),
			 *     then throws an exception that becomes a JSON-RPC error object.
			 * @function
			 * @param {*} assets -- an AssetSets collection, a single AssetSet model, or raw object/array data to instantiate one of those
			 * @param {*} options -- a DownloadOptions collection, a single DownloadOption model, or raw object/array data to instantiate one of those
			 * @param {String} archiveName -- String of archive name to create
			 * @param {String} justification -- Download justification data.  Can be null or empty if no justification is needed.
			 * @param {Object} syncOptions -- any options for Backbone.Sync
			 * @throws JustificationRequired -- if a justification data is required for this download, and none was provided
			 * @throws ApprovalRequired -- if approval is required for this download.
			 * @return {String} It returns a promise that resolves to a Progress UUID (as String)
			 * @todo: the API call works with an array of assetSets.  The AssetSets collection should have this method, and then the model
			 * method can use the collection version (since we're creating a collection anyway (for some reason).)
			 */
			prepareDownload(downloadOptions, archiveName, justification, opts) {
				const assetSets = [this.self()];
				downloadOptions = _.toCollection(
					downloadOptions,
					DownloadOptionsCollection,
				);
				justification = justification || '';

				return [
					'prepareDownload',
					assetSets,
					downloadOptions,
					archiveName,
					justification,
				];
			},
		};
	}

	/**
	 * @property {object}   parsers                     - Model parsers
	 * @property {function} parsers.getAssetIdsInSets   - Parse the return of asset ids
	 * @property {function} parsers.getDownloadOptions  - Parse the return of download options
	 */
	get parsers() {
		return {
			/**
			 * @functon
			 * @param {array} results Asset ids
			 * @param {object} opts Request options
			 */
			getAssetIdsInSets(results, opts) {
				if (!_.isArray(results)) {
					throw new Rpc.ParseError('getAssetIdsInSets() returned non-array');
				}
				this.set({assetIds: results});
			},
			/**
			 * Filters the download options to only include the ones in displayedDownloadOptionTypes and turns the results into a collection
			 * @function
			 * @param {array} downloadOptions array of download options
			 * @param {object} opts Request options
			 * @return {Netx.Collections.DownloadOptions} Download options collection
			 */
			getDownloadOptions(downloadOptions, opts) {
				// this should never be a null array.
				if (downloadOptions == null || !downloadOptions.length) {
					throw new Rpc.ParseError('bad response from getDownloadOptions');
				}
				// Filter returned download options by the array of options types we want to show the user
				// TODO: Currently "manualRepurpose" is not in this list - though we may not want to show it, it still counts as a downloadOption
				// I am forcing it into `displayedDownloadOptionTypes` when the `assetSet` is created for the image being repurposed
				// I think that `displayedDownloadOptionTypes` should be taken into consideration in the controller or view and not be causing
				// data to not get back from the server that is needed. The model should not be concerned with what is displayed in the UI
				// Maybe it is the wrong name for the property - maybe it should be `supportedDownloadOptionTypes`?
				downloadOptions = _.filter(downloadOptions, (downloadOption) =>
					_.contains(this.displayedDownloadOptionTypes, downloadOption.type),
				);
				// Turn into a proper collection
				this.downloadOptions.set(downloadOptions);
				return this.downloadOptions;
			},
			/**
			 * Check that we have any assets left to download/create a link with
			 * @function
			 * @param {object} warnings Warnings
			 * @param {object} opts Request options
			 */
			getDownloadWarnings(warnings, opts) {
				if (!warnings.messageCount) return warnings;

				const assetsUnavailableCount = _.unique(
					_.map(warnings.progressMessages, (message) => message.objectId),
				).length;

				// TODO: we need to be passing the thing that made the assetSet
				// as a target arguments
				// Then we can pass some logic off to the collection/model that
				// the asset set spawned from
				if (assetsUnavailableCount) {
					let abort = false;
					const assetCount = this.get('assetCount') || 0;
					const ids = this.get('ids') || [];
					const type = this.get('type');
					const types = assetSetTypes;

					switch (type) {
						case types.cart: {
							abort =
								Netx.getCurrentCart().getTotal() <= assetsUnavailableCount;
							break;
						}
						case types.category:
							abort = assetCount <= assetsUnavailableCount;
							break;
						case types.savedCart: {
							const cart = Netx.cart.saved.all.get(ids[0]);
							abort =
								cart && cart.get('contentCount') <= assetsUnavailableCount;
							break;
						}
						case types.savedSearch: {
							const search = allSavedSearches.get(ids[0]);
							abort =
								search && search.get('contentCount') <= assetsUnavailableCount;
							break;
						}
						default: {
							abort = ids.length <= assetsUnavailableCount;
						}
					}

					if (abort) {
						warnings.abort = new Error(
							i18n.t('i18n.noAssetsAvailableForDownload'),
						);
					}
				}
				return warnings;
			},
		};
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		/**
		 * Contains the downloadOptions returned by getDownloadOptions
		 * @memberOf module:asset/models/assetSet
		 * @alias downloadOptions
		 * @type {Netx.Collections.DownloadOptions}
		 */
		this.downloadOptions =
			opts.downloadOptions || new DownloadOptionsCollection([]);
		/**
		 * All of the download option types to filter by after getting download options. Set to all by default(but will only show what the user has permission for).
		 * @memberOf module:asset/models/assetSet
		 * @type {Array}
		 */
		this.displayedDownloadOptionTypes =
			opts.displayedDownloadOptionTypes || _.values(downloadOptionTypes);
	}
	/**
	 * @override
	 * @return {object}
	 */
	self() {
		const attrs = super.self();

		// getDownloadOptions does not work with type assetSet however it does work with asset and will accept multiple ids.
		if (attrs.type === 'assetSet') {
			attrs.type = 'asset';
		}
		return attrs;
	}

	/**
	 * @method
	 * @param {string} type Option type
	 * @return {boolean} Has option
	 */
	hasOption(type) {
		return !!this.downloadOptions.findWhere({type: type});
	}

	/**
	 * For backwards compatibility with the shareways system, this method returns a new Shareable model
	 * based on the content of this assetSet model.
	 * @method
	 * @returns {Netx.Models.Shareable}
	 */
	toShareable() {
		let sType;
		switch (this.get('type')) {
			case 'cart':
				sType = 'current cart';
				break;
			case 'savedCart':
				sType = 'saved cart';
				break;
			case 'savedSearch':
				sType = 'saved search';
				break;
			case 'asset':
				sType = 'assets';
				break;
			case 'category':
				// No shareable for catagory!
				return undefined;
		}

		return new ShareableModel({
			ids: this.get('ids'),
			model: this.get('models')[0],
			models: this.get('models'),
			label: this.get('label'),
			type: sType,
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'AssetSet';
	}
}
